import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useStateHandler from '@/hooks/useStateHandler';
import GalleryService from '@/services/gallery.service';
import UserService from '@/services/user.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';

import SEA_DANCING from './images/sea-dancing.png';
import SEA_FOUNTAIN from './images/sea-fountain.png';
import SEA_STARRY_VIOLET from './images/sea-starry-violet.png';
import SEA_SWINGING from './images/sea-swinging.png';
import HeaderCollector from './HeaderCollector';
import HeaderGallery from './HeaderGallery';
import OnBoardingButton from './OnBoardingButton';

import './styles/OnBoardingThemeSelector.css';

const themesConfiguration = {
  "starry_moon": {
    imageSrc: SEA_STARRY_VIOLET,
  },
  "dancing-on-the-grass": {
    imageSrc: SEA_DANCING,
  },
  "fountain-of-delights": {
    imageSrc: SEA_FOUNTAIN
  },
  "swinging-dress": {
    imageSrc: SEA_SWINGING
  },
  "water-lily-pond": {
    imageSrc: SEA_STARRY_VIOLET
  },
};

export default function OnBoardingThemeSelector({ user_type }) {
  const { state, stateHandler, cacheHandler } = useStateHandler();
  const navigate = useNavigate();

  const [selectedTheme, setSelectedTheme] = useState("");
  const [seaSrc, setSeaSrc] = useState(SEA_STARRY_VIOLET);
  const [username, setUsername] = useState(state.user_session.user_username || "");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const changeTheme = (theme) => {
    stateHandler.set("user_session.user_preferences.theme", theme);
    setSelectedTheme(theme);
    if(theme in themesConfiguration) {
      setSeaSrc(themesConfiguration[theme].imageSrc);
    }
  };

  const isActive = (theme) => {
    return selectedTheme && state.user_session.user_preferences.theme === theme;
  };

  const isDiveIntoSuarteDisabled = !selectedTheme || !username;

  const onDiveIntoSuarteClick = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null); 
  
    const accountData = {
      user_username: username,
      user_preferences: {
        theme: selectedTheme
      }
    };
  
    const { response, data } = await UserService.createAccountAndCompleteOnboarding(accountData, user_type);
  
    if(response.ok) {
      stateHandler.set("user_session", data);
      UserService.updateUserPreferences({ "user_preferences.theme": selectedTheme });
      cacheHandler.storeInCache("canUserCompleteOnboarding", true, { type: "@cache/no-expiration" });
    } else if(response.status === 400) {
      setError({ element: data.error_data.element, error_code: data.error_type });
    } else if([401, 403].includes(response.status)) {
      navigate("/", { replace: true });
    }
    setLoading(false);
  };

  const onGalleryClick = async () => {     
    setLoading(true);
    const { response, data } = await GalleryService.completeOnboarding(); 
    if(response.ok) {
      stateHandler.set("user_session", data);
      cacheHandler.storeInCache("canUserCompleteOnboarding", true, { type: "@cache/no-expiration" });
    }else if(response.status === 400) {
      setError({ element: data.error_data.element, error_code: data.error_type });
    }
    else if([401, 403].includes(response.status)) {
      navigate("/", { replace: true });
    }
    setLoading(false);
  };

  // Decide which header to render based on headerType
  const renderHeader = () => {
    switch (user_type) {
    case 'gallery':
      return <HeaderGallery selectedTheme={selectedTheme} state={state} />;
    case 'collector':
      return <HeaderCollector selectedTheme={selectedTheme} error={error} state={state} username={username} setUsername={setUsername} />;
    default:
      return <HeaderCollector selectedTheme={selectedTheme} error={error} state={state} username={username} setUsername={setUsername} />; // Default header
    }
  };

  const onSubmitClick = (e) => {
    switch (user_type) {
    case 'gallery':
      return onGalleryClick(e);
    case 'collector':
      return onDiveIntoSuarteClick(e);
    default:
      return onDiveIntoSuarteClick(e); // Default header
    }
  };

  return (
    <>
      {renderHeader()}

      <p className="onboarding-theme__chooseparagraph">Choose a theme</p>
      <div className={`onboarding_selecting-theme-container`}>
        <OnBoardingButton className="mb-15 starry" isActive={isActive("starry_moon")} onClick={()=>{changeTheme("starry_moon");}} fullWidth={true}>
          Starry moon
        </OnBoardingButton>
        <OnBoardingButton className="mb-15 dancing" isActive={isActive("dancing-on-the-grass")} onClick={()=>{changeTheme("dancing-on-the-grass");}} fullWidth={true}>
          Dancing on the grass
        </OnBoardingButton>
        <OnBoardingButton className="mb-15 fountain" isActive={isActive("fountain-of-delights")} onClick={()=>{changeTheme("fountain-of-delights");}} fullWidth={true}>
          Fountain of delights
        </OnBoardingButton>
        <OnBoardingButton className="mb-15 water" isActive={isActive("swinging-dress")} onClick={()=>{changeTheme("swinging-dress");}} fullWidth={true}>
          Swinging dress
        </OnBoardingButton>
        <OnBoardingButton className="mb-15 violet" isActive={isActive("water-lily-pond")} onClick={()=>{changeTheme("water-lily-pond");}} fullWidth={true}>
          Water lily pond
        </OnBoardingButton>
      </div>
      <div className="onboarding_sea-icon">
        <img src={seaSrc} alt='sea-icon' />
      </div>
      <div className="onboarding_dive-button-container">
        <ContinueButton loading={loading} className={`onboarding_dive-button`} onClick={onSubmitClick} turnOff={isDiveIntoSuarteDisabled}>
                    Dive into Suarte
        </ContinueButton>
      </div>
    </>
  );
}
